/**
 * Retiring the classic crosstab tool in favour of AI Crosstabs. Spec:
 * docs/pdlc/crosstabs-retire-legacy.md, and docs/pdlc/crosstabs-access-lifecycle.md for who has it.
 *
 * Who has AI Crosstabs (M4S-5777 X1, decision A1): MFour staff always, and a client once the
 * classic tool is retired for them. The route rule underneath is the Studio rule (staff or
 * `hasStudioEntitlement`, `AI_CROSSTABS_ROUTES`). No other flag decides it: the old ai-crosstabs
 * flag is not read anywhere (`legacy-retirement.guard.test.ts`).
 *
 * One switch, `crosstabs-legacy-retired`. Off, nothing changes for clients. On, no link, button or
 * list row leads into the classic editor (`/crosstab/<key>`):
 *
 * - AI Crosstabs is on for that user (`crosstabsMode`).
 * - A classic crosstab is still listed. Opening one goes through `/ai/crosstabs/open/<key>`, which
 *   starts a new AI crosstab on its project with the classic import open on it (`openTarget`).
 * - An old `/crosstab/<key>` bookmark converts on open too (`classicUrlTarget`, X4). Until the
 *   switch is on it still opens the classic editor (K7: the bookmark stays until retirement).
 *
 * Pure: no React and no requests. The pages and the opener call these.
 */

export const LEGACY_CROSSTABS_RETIRED_FLAG = 'crosstabs-legacy-retired';

export interface CrosstabsMode {
    /** AI Crosstabs is open to this user. */
    aiCrosstabs: boolean;
    /** The classic tool is retired: no entry point may lead into its editor. */
    legacyRetired: boolean;
}

export interface CrosstabsViewer {
    /** MFour staff (`session.user.isInternal`). */
    isInternal: boolean;
}

/**
 * Whether AI Crosstabs is on for this viewer, and whether the classic tool is retired. Staff always
 * have AI Crosstabs; a client has it once the classic tool is retired. Nothing else is read.
 */
export function crosstabsMode(
    hasFlag: (key: string) => boolean,
    viewer: CrosstabsViewer,
): CrosstabsMode {
    const legacyRetired = hasFlag(LEGACY_CROSSTABS_RETIRED_FLAG);
    return { aiCrosstabs: viewer.isInternal === true || legacyRetired, legacyRetired };
}

/** On a new AI crosstab's URL: open the classic import on the classic crosstab with this key. */
export const CONVERT_PARAM = 'convert';

/** Hover text on a classic crosstab's chip in the Crosstabs list once the tool is retired. */
export const CLASSIC_CHIP_TITLE = "Opens as a new AI crosstab with this crosstab's rows and banner";

/** Opens any saved crosstab, AI or classic, in AI Crosstabs. Where it lands is `openTarget`. */
export function crosstabOpenPath(key: string): string {
    return `/ai/crosstabs/open/${encodeURIComponent(key)}`;
}

/**
 * Where a saved crosstab opens for this user. An AI crosstab opens in AI Crosstabs when that is on
 * for them; a classic one opens there once the classic tool is retired. Everything else opens in
 * the classic editor, as it always has.
 */
export function savedCrosstabHref(
    item: { id: string; hasAiLayout: boolean | null | undefined },
    mode: CrosstabsMode,
): string {
    const inAi = item.hasAiLayout === true ? mode.aiCrosstabs : mode.legacyRetired;
    return inAi ? crosstabOpenPath(item.id) : `/crosstab/${item.id}`;
}

/**
 * Where an old classic editor address (`/crosstab/<key>`) sends its visitor (M4S-5777 X4): to the
 * opener once the classic tool is retired, which converts a classic crosstab and opens an AI one;
 * null while it is not, so the classic editor answers its own URL until retirement (K7).
 */
export function classicUrlTarget(key: string, mode: CrosstabsMode): string | null {
    const k = String(key ?? '').trim();
    if (!k || !mode.legacyRetired) return null;
    return crosstabOpenPath(k);
}

/**
 * BT-2204: the way back from the classic editor. The workspace's **Classic view** names the
 * crosstab it left in `?from=`, and the classic editor's **Classic view** switch, turned off, goes
 * back there.
 */
export const CLASSIC_FROM_PARAM = 'from';

/** The only address Classic view goes back to: a workspace path on this site, never a full URL. */
const WORKSPACE_PATH = /^\/ai\/crosstabs\/project\/[^/?#\\]+(\?[^#\\]*)?$/;

/** The flags that make a workspace address start something rather than open a saved crosstab. */
const STARTING_FLAGS = ['open', 'new', 'copy', CONVERT_PARAM, 'then', 'type', 'start', 'dani'];

/**
 * The workspace address to come back to from Classic view: the crosstab this session saves to,
 * opened by its key (`layout=`). Null while the session has no record yet, because an address
 * without a key would create a new crosstab. The flags that start a create, a copy, a conversion,
 * a Duplicate or a DANI plan are dropped, and so is `name=` (a name in the address wins over the
 * saved one), so coming back opens the crosstab as it was saved. `surveys=` stays.
 */
export function workspaceReturnPath(
    pathname: string,
    search: string,
    boundKey: string | null | undefined,
): string | null {
    const key = String(boundKey ?? '').trim();
    if (!key) return null;
    const params = new URLSearchParams(search);
    for (const flag of [...STARTING_FLAGS, 'name']) params.delete(flag);
    params.set('layout', key);
    const path = `${pathname}?${params.toString()}`;
    return WORKSPACE_PATH.test(path) ? path : null;
}

/** The workspace's Classic view: the classic editor on `key`, told where to come back to. */
export function classicViewHref(key: string, returnPath: string | null): string {
    const editor = `/crosstab/${encodeURIComponent(key)}`;
    return returnPath
        ? `${editor}?${CLASSIC_FROM_PARAM}=${encodeURIComponent(returnPath)}`
        : editor;
}

/**
 * Where the classic editor's Classic view switch goes when it is turned off: back to the workspace
 * that opened it when `from` names one; otherwise this crosstab in AI Crosstabs (`crosstabOpenPath`,
 * which offers an earlier conversion before it makes a new one).
 */
export function classicViewOffTarget(key: string, from: string | null | undefined): string {
    const back = String(from ?? '').trim();
    return WORKSPACE_PATH.test(back) ? back : crosstabOpenPath(key);
}

/**
 * The prefix AI Crosstabs' create path stores on a crosstab's name. It is never shown: the
 * Crosstabs list strips it (`stripDaniPrefix`, copied here because AI Crosstabs code never imports
 * from `src/@crosstab/`). Comparing a stored name with a shown one without stripping it never
 * matches.
 */
const DANI_NAME_PREFIX = 'DANI: ';

/** A crosstab's name as the Crosstabs list shows it. */
export function shownName(name: string): string {
    return name.startsWith(DANI_NAME_PREFIX) ? name.slice(DANI_NAME_PREFIX.length).trim() : name;
}

/** The name a converted crosstab starts with. The import gives it the same one. */
export function convertedName(name: string): string {
    const base = shownName(name).trim() || 'Untitled Crosstab';
    return base.endsWith(' (AI)') ? base : `${base} (AI)`;
}

/** What the opener reads about a saved crosstab, from `GET /api/studio/v1/crosstab/<key>`. */
export interface SavedCrosstabRecord {
    key: string;
    name: string;
    /** The numeric project id. */
    projectId?: string | null;
    /** The one survey the crosstab was made on, when it names one. */
    surveyId?: string | null;
    /** `crosstab_layout.ai_layout`. Present on an AI crosstab and on nothing else (M4S-5579). */
    aiLayout?: unknown;
    /** M4S-5777 X133: the record's org; null for a staff draft. */
    orgId?: number | null;
}

/**
 * The surveys a crosstab is loaded on: its own survey when it names one, else its project's. The
 * same rule as the Crosstabs tiles' `aiWorkspaceSurveyScope`, copied rather than imported because
 * AI Crosstabs code never imports from `src/@crosstab/`.
 */
export function surveysFor(
    ownSurveyId: string | number | null | undefined,
    projectSurveyIds: readonly (string | number)[] | null | undefined,
): string | null {
    const own = ownSurveyId == null ? '' : String(ownSurveyId).trim();
    if (own) return own;
    const fromProject = (projectSurveyIds ?? []).map((s) => String(s).trim()).filter(Boolean);
    return fromProject.length ? fromProject.join(',') : null;
}

/**
 * Where the opener sends a saved crosstab, or null when it cannot say (no project, or no survey
 * to load it on).
 *
 * The workspace is addressed by the numeric project id with its surveys named. Naming the surveys
 * is what lets a numeric address save: the data route only sets `numericProjectId` from the
 * address on that path. It also keeps the load on this one project, where an account address with
 * no surveys spans every project on the account.
 *
 * - An AI crosstab opens by key (`layout=`), as the Crosstabs tiles open it. No `name=`: a name in
 *   the address wins over the saved one.
 * - A classic crosstab becomes a new AI crosstab named after it, with `convert=<key>`. The
 *   workspace opens the classic import on that crosstab once the new one can be edited.
 */
export function openTarget(
    record: SavedCrosstabRecord,
    projectSurveyIds: readonly (string | number)[] | null,
): string | null {
    const projectId = String(record.projectId ?? '').trim();
    if (!projectId) return null;
    const surveys = surveysFor(record.surveyId, projectSurveyIds);
    if (!surveys) return null;

    const params = new URLSearchParams();
    if (record.aiLayout != null) {
        params.set('surveys', surveys);
        params.set('layout', record.key);
    } else {
        params.set('name', convertedName(record.name));
        params.set('surveys', surveys);
        params.set(CONVERT_PARAM, record.key);
    }
    return `/ai/crosstabs/project/${encodeURIComponent(projectId)}?${params.toString()}`;
}
